import {
  Callout,
  Classes,
  FormGroup,
  H5,
  InputGroup,
  Intent,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { AdvancedOptions } from 'src/components/AdvancedOptions';
import {
  MAX_CSV_PARSE_LINES,
  MAX_CSV_VISIBLE_LINES,
} from 'src/constants/limits';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectFileUploadOptionsForm } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadOptionsForm';
import { ProjectFileUploadTable } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadTable';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { doesFileHaveDuplicateHeaders } from 'src/utils/csv/doesFileHaveDuplicateHeaders';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useProjectFileUploadParser } from 'src/utils/hooks/useProjectFileUploadParser';

export type ProjectFileUploadPanelRefAttributes = {
  abortParser: () => void;
} | null;

export type ProjectFileUploadPanelProps = {
  existingFields?: ProjectFileUploadField[];
  disableEditing?: boolean;
  isFilePartial: boolean;
  file: string | File | null;
  fileName: string;
  fileOptions: ProjectFileUploadOptions;
  filePath: string;
  setErrorText: (errorText: string) => void;
  setFields: (fields: ProjectFileUploadField[]) => void;
  setFileName: (fileName: string) => void;
  setFileOptions: (fileOptions: ProjectFileUploadOptions) => void;
  setRows: (rows: string[][]) => void;
};
// eslint-disable-next-line react/display-name
export const ProjectFileUploadPanel = React.forwardRef<
  ProjectFileUploadPanelRefAttributes,
  ProjectFileUploadPanelProps
>(
  (
    {
      existingFields,
      disableEditing,
      isFilePartial,
      file,
      fileName,
      fileOptions,
      filePath,
      setErrorText,
      setFields,
      setFileName,
      setFileOptions,
      setRows,
    },
    ref
  ) => {
    const [isFileWithDuplicateHeaders, setIsFileWithDuplicateHeaders] =
      React.useState(false);

    const { abortParser, isParsing, errors, fields, rows, updateFieldLabel } =
      useProjectFileUploadParser(
        file,
        fileOptions,
        isFilePartial ? MAX_CSV_PARSE_LINES : undefined,
        existingFields
      );
    React.useImperativeHandle(ref, () => ({
      abortParser: abortParser.current,
    }));

    const didFieldsChange = useDidValueChange(fields, isEqual);
    React.useEffect(() => {
      if (didFieldsChange) {
        setFields(fields);
      }
    }, [didFieldsChange, fields, setFields]);

    const didRowsChange = useDidValueChange(rows, isEqual);
    React.useEffect(() => {
      if (didRowsChange) {
        setRows(rows);
      }
    }, [didRowsChange, rows, setRows]);

    let preview: JSX.Element | undefined;
    let visibleRows = [];
    if (isParsing) {
      preview = <Spinner size={SpinnerSize.LARGE} />;
    } else if (!fileOptions.separator) {
      preview = (
        <Callout
          intent={Intent.DANGER}
          title={
            copy.components.projectResourceDialog.projectFileUploadPanel
              .validation.separator
          }
        />
      );
    } else if (!fileOptions.quote) {
      preview = (
        <Callout
          intent={Intent.DANGER}
          title={
            copy.components.projectResourceDialog.projectFileUploadPanel
              .validation.quote
          }
        />
      );
    } else if (!fileOptions.escape) {
      preview = (
        <Callout
          intent={Intent.DANGER}
          title={
            copy.components.projectResourceDialog.projectFileUploadPanel
              .validation.escape
          }
        />
      );
    } else if (errors.length) {
      preview = (
        <Callout
          intent={Intent.DANGER}
          title={
            copy.components.projectResourceDialog.projectFileUploadPanel.errors
              .title
          }
        >
          <AdvancedOptions
            text={() =>
              copy.components.projectResourceDialog.projectFileUploadPanel
                .errors.details
            }
          >
            {errors.map((error, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx}>
                {
                  copy.components.projectResourceDialog.projectFileUploadPanel
                    .errors.row
                }{' '}
                {error.row} - {error.message}
              </div>
            ))}
          </AdvancedOptions>
        </Callout>
      );
    } else if (rows.length) {
      visibleRows = rows.slice(0, MAX_CSV_VISIBLE_LINES);
      preview = (
        <ProjectFileUploadTable
          disableEditing={disableEditing}
          fields={fields}
          onFieldLabelChange={updateFieldLabel}
          rows={visibleRows}
        />
      );
    } else if (filePath) {
      preview = (
        <Callout
          intent={Intent.WARNING}
          title={
            copy.components.projectResourceDialog.projectFileUploadPanel.errors
              .empty
          }
        />
      );
    }

    React.useEffect(() => {
      if (!fileOptions.separator) {
        setErrorText(
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.separator
        );
      } else if (!fileOptions.quote) {
        setErrorText(
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.quote
        );
      } else if (!fileOptions.escape) {
        setErrorText(
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.escape
        );
      } else if (errors.length) {
        setErrorText(
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.errors
        );
      } else if (!rows.length) {
        setErrorText(
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.empty
        );
      } else if (!fileName) {
        setErrorText(
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.name
        );
      } else {
        setErrorText('');
      }
    }, [fileOptions, errors, rows, fileName, setErrorText]);

    const didFileContentsChange = useDidValueChange(file);
    const didFileOptionsChange = useDidValueChange(fileOptions);
    React.useEffect(() => {
      if (!file) {
        return;
      }

      if (didFileContentsChange || didFileOptionsChange) {
        doesFileHaveDuplicateHeaders(file, fileOptions).then(
          (hasDuplicateHeaders) => {
            setIsFileWithDuplicateHeaders(hasDuplicateHeaders);
          }
        );
      }
    }, [didFileOptionsChange, didFileContentsChange, fileOptions, file]);

    const didHasDuplicateHeadersChange = useDidValueChange(
      isFileWithDuplicateHeaders
    );
    React.useEffect(() => {
      if (
        didHasDuplicateHeadersChange &&
        isFileWithDuplicateHeaders &&
        Boolean(fileOptions.header)
      ) {
        setFileOptions({
          ...fileOptions,
          header: false,
        });
      }
    }, [
      didHasDuplicateHeadersChange,
      fileOptions,
      setFileOptions,
      isFileWithDuplicateHeaders,
    ]);

    return (
      <div className="sd-project-file-upload-panel">
        <div className="sd-project-file-upload-panel__scroll-wrapper">
          <FormGroup
            inline
            label={
              copy.components.projectResourceDialog.projectFileUploadPanel.form
                .path
            }
            labelFor="sd-project-file-upload-panel-path"
          >
            <InputGroup
              disabled
              fill
              id="sd-project-file-upload-panel-path"
              name="path"
              value={filePath}
            />
          </FormGroup>
          <FormGroup
            inline
            label={
              copy.components.projectResourceDialog.projectFileUploadPanel.form
                .name
            }
            labelFor="sd-project-file-upload-panel-name"
          >
            <InputGroup
              disabled={disableEditing}
              fill
              id="sd-project-file-upload-panel-name"
              name="name"
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                setFileName(evt.target.value);
              }}
              value={fileName}
            />
          </FormGroup>
          <div
            className={classNames(
              'sd-project-file-upload-panel__resource-properties',
              Classes.CALLOUT
            )}
          >
            <H5>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .form.resourceProperties
              }
            </H5>
            <ProjectFileUploadOptionsForm
              disableEditing={Boolean(disableEditing)}
              disableHeaderToggle={isFileWithDuplicateHeaders}
              fileOptions={fileOptions}
              onSetFileOptions={setFileOptions}
            />
          </div>
          <div
            className={classNames(
              'sd-project-file-upload-panel__preview',
              Classes.CALLOUT
            )}
          >
            <div className="sd-project-file-upload-panel__preview__header">
              <H5>
                {
                  copy.components.projectResourceDialog.projectFileUploadPanel
                    .form.preview
                }
              </H5>
              <div data-testid={TEST_IDS.projectFileUploadPanel.rowCount}>
                {visibleRows.length > 0
                  ? copy.components.projectResourceDialog.projectFileUploadPanel.form.previewRowCount(
                      visibleRows.length,
                      !isFilePartial && visibleRows.length === rows.length
                    )
                  : ''}
              </div>
            </div>
            {preview}
          </div>
        </div>
      </div>
    );
  }
);
